import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router';
import {
  GUARANTEES_APPLICATION_CONTRAGENT_QUERY_PARAM,
  guaranteesApplicationStatusConfig,
  guaranteesClientText,
} from '~/modules/guarantees/form/client/constants.ts';
import { getStringValue } from '~/modules/guarantees/utils.ts';
import {
  selectGuaranteesAdministrationRoles,
  selectGuaranteesAdministrationRolesStatus,
  selectGuaranteesApprovalComment,
  selectGuaranteesApprovalCommentError,
  selectGuaranteesConclusionTemplateDocumentError,
  selectGuaranteesCurrentApplication,
  selectGuaranteesCurrentApplicationError,
  selectGuaranteesCurrentApplicationStatus,
  selectGuaranteesGenerateConclusionAttachmentStatus,
} from '~/store/guarantees/selectors.ts';
import {
  fetchGuaranteesApplication,
  fetchGuaranteesApplicationFiles,
  fetchGuaranteesAdministrationRoles,
  fetchGuaranteesApprovalComment,
  fetchGuaranteesConclusionTemplateDocument,
} from '~/store/guarantees/thunks/applicationThunks.ts';
import {
  GuaranteesApplicationStatus,
  GuaranteesFetchStatus,
  GuaranteesUdbtfApprovalStatus,
  type TGuaranteesApplication,
  type TGuaranteesApplicationFile,
} from '~/store/guarantees/types.ts';
import { normalizeGuaranteesApplicationStatus } from '~/store/guarantees/utils.ts';
import type { AppDispatch } from '~/store/store.ts';
import { useApplicationRequestFeedback } from '../../shared/hooks/useApplicationRequestFeedback.ts';
import { useFreshGuaranteesRequestError } from '../../shared/hooks/useFreshGuaranteesRequestError.ts';
import { getCurrentRouteGuaranteesApplication } from '../../shared/utils/applicationIdentity.ts';
import { useGuaranteesApplicationFilesSync } from '../shared/SidePanel/applicationFilesSync.ts';
import {
  INTERNAL_APPROVAL_LETTER_CONFIG,
  UDBTF_APPROVAL_LETTER_GET_STARTED_PATH,
  UDBTF_APPROVAL_LETTER_TASKS_PATH,
} from './constants.ts';
import type { TInternalApprovalLetterConfig, TInternalApprovalType } from './types.ts';
import {
  getInternalApprovalPageLoading,
  resolveInternalApprovalRenderApplication,
  resolveInternalApprovalRedirectPath,
} from './utils.ts';

const isInternalApprovalType = (value?: string): value is TInternalApprovalType =>
  value === 'udbtf' || value === 'uo' || value === 'umvn';

const getInternalApprovalStatus = (
  application: ReturnType<typeof selectGuaranteesCurrentApplication>,
  approvalType: TInternalApprovalType
): string => {
  if (approvalType === 'udbtf') {
    return getStringValue(application?.udbtfApprovalStatus);
  }

  if (approvalType === 'uo') {
    return getStringValue(application?.uoApprovalStatus);
  }

  return getStringValue(application?.umvnApprovalStatus);
};

export const useInternalApprovalPage = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const { applicationId = '', approvalType: routeApprovalType } = useParams();
  const approvalType: TInternalApprovalType = isInternalApprovalType(routeApprovalType)
    ? routeApprovalType
    : 'udbtf';
  const approvalConfig: TInternalApprovalLetterConfig =
    INTERNAL_APPROVAL_LETTER_CONFIG[approvalType];
  const isUnknownApprovalType = !!routeApprovalType && !isInternalApprovalType(routeApprovalType);
  const contragentId =
    new URLSearchParams(location.search).get(GUARANTEES_APPLICATION_CONTRAGENT_QUERY_PARAM) ?? '';
  const application = useSelector(selectGuaranteesCurrentApplication);
  const applicationStatus = useSelector(selectGuaranteesCurrentApplicationStatus);
  const applicationError = useSelector(selectGuaranteesCurrentApplicationError);
  const approvalComment = useSelector(selectGuaranteesApprovalComment);
  const approvalCommentError = useSelector(selectGuaranteesApprovalCommentError);
  const conclusionTemplateDocumentError = useSelector(
    selectGuaranteesConclusionTemplateDocumentError
  );
  const generateConclusionAttachmentStatus = useSelector(
    selectGuaranteesGenerateConclusionAttachmentStatus
  );
  const administrationRoles = useSelector(selectGuaranteesAdministrationRoles);
  const administrationRolesStatus = useSelector(selectGuaranteesAdministrationRolesStatus);
  const [applicationFiles, setApplicationFiles] = useState<TGuaranteesApplicationFile[]>([]);
  const [isApplicationFilesLoading, setIsApplicationFilesLoading] = useState(false);
  const [hasApplicationFilesLoaded, setHasApplicationFilesLoaded] = useState(false);
  const [freshApplicationKey, setFreshApplicationKey] = useState('');
  const [renderApplication, setRenderApplication] = useState<TGuaranteesApplication | null>(null);
  const applicationRequestKey = `${contragentId}:${applicationId}:${approvalType}`;
  const { currentRouteApplication, isLoadedApplicationCurrent } =
    getCurrentRouteGuaranteesApplication(application, applicationId);
  const currentRenderApplication = resolveInternalApprovalRenderApplication({
    currentRouteApplication,
    previousRenderApplication: renderApplication,
    routeApplicationId: applicationId,
  });
  const hasApprovalAdministratorRole = administrationRoles.includes(approvalConfig.role);
  const baseRedirectPath = isUnknownApprovalType
    ? UDBTF_APPROVAL_LETTER_TASKS_PATH
    : !contragentId
      ? UDBTF_APPROVAL_LETTER_GET_STARTED_PATH
      : '';
  const effectiveApplicationId =
    getStringValue(currentRenderApplication?.applicationId || currentRenderApplication?.id) ||
    applicationId;
  const applicationNumber =
    getStringValue(currentRenderApplication?.applicationNumber) ||
    getStringValue(currentRenderApplication?.root?.applicationNumber) ||
    effectiveApplicationId;
  const normalizedStatus = normalizeGuaranteesApplicationStatus(
    currentRenderApplication?.applicationStatus
  );
  const freshInternalApprovalStatus = getInternalApprovalStatus(
    currentRouteApplication,
    approvalType
  );
  const isFreshInternalApprovalInProgress =
    freshInternalApprovalStatus === GuaranteesUdbtfApprovalStatus.IN_PROGRESS;
  const hasFreshCurrentApplication =
    freshApplicationKey === applicationRequestKey &&
    applicationStatus === GuaranteesFetchStatus.Success &&
    isLoadedApplicationCurrent;
  const statusConfig =
    guaranteesApplicationStatusConfig[normalizedStatus] ??
    guaranteesApplicationStatusConfig[GuaranteesApplicationStatus.DRAFT];
  const isApplicationLoading = getInternalApprovalPageLoading({
    applicationStatus,
    baseRedirectPath,
    renderApplication: currentRenderApplication,
  });
  const isAdministrationRolesLoading =
    administrationRolesStatus === GuaranteesFetchStatus.Idle ||
    administrationRolesStatus === GuaranteesFetchStatus.Loading;
  const isApprovalAccessLoading = !hasApprovalAdministratorRole && isAdministrationRolesLoading;
  const isApprovalAccessDenied = !isApprovalAccessLoading && !hasApprovalAdministratorRole;
  const {
    modalAlert,
    modalSuccess,
    setModalAlert,
    setModalSuccess,
    showGuaranteesRequestError,
    handleCloseAlertModal,
    handleCloseSuccessModal,
  } = useApplicationRequestFeedback({
    applicationId,
    contragentId,
    dispatch,
  });
  const redirectPath = resolveInternalApprovalRedirectPath({
    baseRedirectPath,
    hasCurrentRouteApplication: !!currentRouteApplication,
    hasFreshCurrentApplication,
    isInternalApprovalInProgress: isFreshInternalApprovalInProgress,
    modalSuccessText: String(modalSuccess.text ?? ''),
    tasksPath: UDBTF_APPROVAL_LETTER_TASKS_PATH,
  });
  const headerProps = useMemo(
    () => ({
      applicationNumber,
      applicationNumberLabel: guaranteesClientText.applicationsNumberLabel,
      applicationIdLabel: guaranteesClientText.applicationIdLabel,
      backLabel: guaranteesClientText.backToApplicationsButton,
      contragentId,
      contragentIdLabel: guaranteesClientText.contragentIdLabel,
      effectiveApplicationId,
      onBack: () => navigate(UDBTF_APPROVAL_LETTER_TASKS_PATH),
      statusConfig,
    }),
    [applicationNumber, contragentId, effectiveApplicationId, navigate, statusConfig]
  );
  const handleCloseUdbtfAccessModal = useCallback(() => {
    handleCloseAlertModal();
    navigate(UDBTF_APPROVAL_LETTER_TASKS_PATH, { replace: true });
  }, [handleCloseAlertModal, navigate]);
  const navigateToTasks = useCallback(() => {
    navigate(UDBTF_APPROVAL_LETTER_TASKS_PATH, { replace: true });
  }, [navigate]);
  const refreshApplicationFiles = useCallback(async () => {
    if (!applicationId || applicationId === 'new') {
      setApplicationFiles([]);
      setHasApplicationFilesLoaded(true);

      return [];
    }

    setIsApplicationFilesLoading(true);

    try {
      const nextApplicationFiles = await dispatch(
        fetchGuaranteesApplicationFiles({ applicationId })
      ).unwrap();

      setApplicationFiles(nextApplicationFiles);
      setHasApplicationFilesLoaded(true);

      return nextApplicationFiles;
    } finally {
      setIsApplicationFilesLoading(false);
    }
  }, [applicationId, dispatch]);

  useGuaranteesApplicationFilesSync(effectiveApplicationId, () => {
    void refreshApplicationFiles().catch(showGuaranteesRequestError);
  });

  useEffect(() => {
    if (currentRouteApplication) {
      setRenderApplication(currentRouteApplication);
    }
  }, [currentRouteApplication]);

  useEffect(() => {
    if (administrationRolesStatus === GuaranteesFetchStatus.Idle) {
      void dispatch(fetchGuaranteesAdministrationRoles());
    }
  }, [administrationRolesStatus, dispatch]);

  useEffect(() => {
    if (!contragentId || !applicationId || applicationId === 'new') {
      return;
    }

    let isActive = true;

    setFreshApplicationKey('');
    void dispatch(fetchGuaranteesApplication({ contragentId, applicationId }))
      .unwrap()
      .then(() => {
        if (isActive) {
          setFreshApplicationKey(applicationRequestKey);
        }
      })
      .catch(() => {
        // The request error is rendered from the guarantees store.
      });

    return () => {
      isActive = false;
    };
  }, [applicationId, applicationRequestKey, contragentId, dispatch]);

  useEffect(() => {
    if (!contragentId || !applicationId || applicationId === 'new') {
      return;
    }

    void refreshApplicationFiles().catch(showGuaranteesRequestError);

    if (approvalType === 'uo' || approvalType === 'umvn') {
      void dispatch(
        fetchGuaranteesConclusionTemplateDocument({
          applicationId,
          approvalType,
        })
      );
      void dispatch(
        fetchGuaranteesApprovalComment({
          applicationId,
          approvalType,
        })
      );
    }
  }, [
    applicationId,
    approvalType,
    contragentId,
    dispatch,
    refreshApplicationFiles,
    showGuaranteesRequestError,
  ]);

  useFreshGuaranteesRequestError(
    applicationError || conclusionTemplateDocumentError || approvalCommentError,
    showGuaranteesRequestError
  );

  useEffect(() => {
    if (isApprovalAccessDenied) {
      setModalAlert({ text: approvalConfig.accessDeniedText });
    }
  }, [approvalConfig.accessDeniedText, isApprovalAccessDenied, setModalAlert]);

  return {
    application: currentRenderApplication,
    applicationFiles,
    applicationId,
    approvalComment,
    approvalConfig,
    approvalType,
    contragentId,
    dispatch,
    effectiveApplicationId,
    generateConclusionAttachmentStatus,
    handleCloseAlertModal,
    handleCloseSuccessModal,
    handleCloseUdbtfAccessModal,
    headerProps,
    isApplicationLoading,
    hasApplicationFilesLoaded,
    isApplicationFilesLoading,
    isUdbtfAccessDenied: isApprovalAccessDenied,
    isUdbtfAccessLoading: isApprovalAccessLoading,
    modalAlert,
    modalSuccess,
    navigateToTasks,
    redirectPath,
    refreshApplicationFiles,
    setModalAlert,
    setModalSuccess,
    showGuaranteesRequestError,
  };
};
